import { DATE_RANGE_PRESETS, isBoundedRange, isWithinRange, resolveDateRange } from '@utils/dateRange';
import { formatUser } from './format';

// Every way the loaded spec list can be narrowed, in one place. There are four predicates — text,
// status, source and now author and updated-window — but only three of them run here: status and
// source are SERVER-side filters (the list endpoint accepts exactly those two), so they have already
// been applied to the rows this module ever sees.
//
// Keeping the predicates, the option set and the "is anything actually filtered" test together is
// what stops the table, the empty-state message and the author dropdown from disagreeing about what
// is on screen. Mirrors src/features/feedView/rowFilters.js.

export const AUTHOR_ALL = 'all';
// `created_by` is null on every seed spec, and null is not a user id — it is the absence of one. It
// gets its own bucket rather than being lumped in with "no opinion", so "show me what the system
// shipped" is a question this control can ask.
export const AUTHOR_SEED = 'seed';
// The reader, whoever that turns out to be. A sentinel rather than their id because the list opens
// on this filter before the session has necessarily said who is reading, and a default that has to
// wait for a lookup is a default that flashes. It resolves against the current user at match time.
export const AUTHOR_MINE = 'mine';

export const EMPTY_LIST_FILTERS = {
  status: 'all',
  source: 'all',
  q: '',
  author: AUTHOR_ALL,
  rangePreset: DATE_RANGE_PRESETS.all,
  rangeFrom: null,
  rangeTo: null,
};

// The one definition of the timestamp this list is ordered and filtered by, shared with the Updated
// column so the three can never drift. `createTimeInstance(null)` returns null rather than a dayjs,
// so every caller has to handle the both-null case — which is why this returns null instead of
// handing one on.
export const updatedAtOf = (spec) => spec?.updated_at || spec?.created_at || null;

// String-keyed because a <select> value is always a string, so `8` and `'8'` collapse to one option.
export const authorKey = (spec) => (spec?.created_by == null ? AUTHOR_SEED : String(spec.created_by));

const mineKey = (currentUserId) => (currentUserId == null ? null : String(currentUserId));

export const authorLabel = (key, currentUserId) => {
  if (key === AUTHOR_SEED) return 'Seed (system)';
  const mine = mineKey(currentUserId);
  // Before the session resolves there is no id to show, but the option still has to read as the
  // reader rather than as `User #mine`.
  if (key === AUTHOR_MINE) return mine === null ? 'You' : `${formatUser(mine)} (you)`;
  if (mine !== null && key === mine) return `${formatUser(key)} (you)`;
  return formatUser(key);
};

// Mine with no current user is a question that cannot be answered yet, so it narrows nothing: the
// session resolves before the list does in practice, and an empty table blaming a filter the reader
// never picked is the worse failure.
export const matchesAuthor = (spec, author, currentUserId) => {
  if (!author || author === AUTHOR_ALL) return true;
  if (author === AUTHOR_MINE) {
    const mine = mineKey(currentUserId);
    return mine === null || authorKey(spec) === mine;
  }
  return authorKey(spec) === author;
};

export const matchesUpdatedRange = (spec, resolvedRange) => isWithinRange(updatedAtOf(spec), resolvedRange);

export const matchesQuery = (spec, q, displayName) => {
  if (!q) return true;
  return (
    displayName.toLowerCase().includes(q) ||
    String(spec?.score_spec_id || '')
      .toLowerCase()
      .includes(q)
  );
};

export const resolveFilterRange = (filters) =>
  resolveDateRange({
    preset: filters?.rangePreset,
    from: filters?.rangeFrom,
    to: filters?.rangeTo,
  });

// Whether anything is actually narrowing the list. Custom-with-no-dates is deliberately NOT active:
// it is a preset the operator has selected but not filled in, and it matches everything, so calling
// it active would make an empty store blame a filter that is not doing anything.
export const isFiltering = (filters) => {
  if (!filters) return false;
  return (
    filters.status !== 'all' ||
    filters.source !== 'all' ||
    (filters.q || '').trim() !== '' ||
    (filters.author || AUTHOR_ALL) !== AUTHOR_ALL ||
    isBoundedRange(resolveFilterRange(filters))
  );
};

// Whether the reader's own specs are the ONLY thing narrowing the list — the state the list opens
// in. It gets its own empty message, because "no specs match these filters" is a strange thing to
// say to someone who has not touched a filter.
export const isOnlyMine = (filters) => {
  if (!filters || filters.author !== AUTHOR_MINE) return false;
  return !isFiltering({ ...filters, author: AUTHOR_ALL });
};

// The options the author select offers: the reader, then the authors the LOADED rows carry, so the
// control can never narrow to nothing it did not name. Ordered rather than sorted — a plain .sort()
// on stringified ids puts User #10 ahead of User #8, and drops 'seed' wherever ASCII happens to
// land it.
//
// The reader is offered whenever the session knows who they are, rows or no rows: it is the default,
// and a default that is not on the list leaves the select showing a choice it is not applying.
// Their option normally carries the Mine sentinel. A URL can name them by raw id instead
// (?author=8, typed or shared by someone else), and that value is kept: it means the same rows, and
// keeps meaning them whoever opens the link, so the option carries it too — or the select would
// show All while narrowed to the reader.
//
// Any other `selected` is appended when it is no longer present, because status and source are
// server-side: changing either can take every one of an author's specs out of the response, and an
// option that silently vanishes leaves the select blank while still filtering. Same rule
// ModelSelect applies to a model that is not in the catalog.
export const authorOptions = (specs, currentUserId, selected) => {
  const keys = new Set((specs || []).map(authorKey));

  const mine = mineKey(currentUserId);
  const offerReader = mine !== null || selected === AUTHOR_MINE;
  const readerValue = mine !== null && selected === mine ? mine : AUTHOR_MINE;
  const hasSeed = keys.has(AUTHOR_SEED);

  const others = Array.from(keys)
    .filter((key) => key !== AUTHOR_SEED && key !== mine)
    .sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });

  const ordered = [...(offerReader ? [readerValue] : []), ...others, ...(hasSeed ? [AUTHOR_SEED] : [])];

  const options = [
    { value: AUTHOR_ALL, label: 'All authors' },
    ...ordered.map((key) => ({ value: key, label: authorLabel(key, currentUserId) })),
  ];

  if (selected && selected !== AUTHOR_ALL && selected !== readerValue && !keys.has(selected)) {
    options.push({
      value: selected,
      label: `${authorLabel(selected, currentUserId)} — no longer in this list`,
    });
  }

  return options;
};

// Offered only when it can do something: with a single author every row is theirs, and a select whose
// one choice is a no-op is noise. It stays on screen while a selection is active, so a filter can
// never become unreachable — see rowFilters.js for the same rule on the feed-row selects. The default
// counts as a selection: a list that opens narrowed to the reader has to say so somewhere.
export const shouldOfferAuthorFilter = (specs, selected) =>
  new Set((specs || []).map(authorKey)).size > 1 || (!!selected && selected !== AUTHOR_ALL);
